// ================= SECTION 1: IMPORTS =====================
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { FiUser, FiGlobe, FiChevronDown } from 'react-icons/fi';
import { RiLoginCircleLine, RiLogoutCircleLine } from 'react-icons/ri';
import AuthModal from '../../modules/auth/AuthModal';
import { auth } from '../../modules/auth/firebaseConfig';
import M2TLogo from '../common/M2TLogo';

// ================= SECTION 2: COMPONENT START =====================
export default function GlobalHeader() {
  // ================= SECTION 2A: STATE =====================
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [startAuthMode, setStartAuthMode] = useState('login');
  const [selectedLanguage, setSelectedLanguage] = useState('EN');
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const centerButtons = ['Learn', 'Earn', 'Connect', 'Promote', 'Grow', 'Launch'];
  const languages = [
    { code: 'EN', label: 'English' }, { code: 'HI', label: 'हिंदी' },
    { code: 'TA', label: 'தமிழ்' }, { code: 'TE', label: 'తెలుగు' },
    { code: 'KN', label: 'ಕನ್ನಡ' }, { code: 'ML', label: 'മലയാളം' },
    { code: 'BN', label: 'বাংলা' }, { code: 'MR', label: 'मराठी' },
  ];

  // ================= SECTION 2B: EFFECTS =====================
  useEffect(() => {
    document.documentElement.classList.add('dark');
    const unsubscribe = onAuthStateChanged(auth, (user) => setCurrentUser(user));
    return () => unsubscribe();
  }, []);

  // ================= SECTION 2C: FUNCTIONS =====================
  const handleLogout = async () => {
    await signOut(auth);
    setCurrentUser(null);
    localStorage.removeItem('fromDashboard');
    navigate('/');
  };

  const isGuest = currentUser && currentUser.isAnonymous;
  const cameFromDashboard = localStorage.getItem('fromDashboard') === 'true';

  // ================= SECTION 3: RENDER =====================
  return (
    <>
      <header className="relative w-full flex flex-wrap items-center justify-between px-6 py-4 bg-black dark:bg-gray-900 text-white border-b border-gray-700">
        
        {/* ================= SECTION 3B: LOGO ===================== */}
        <div className="flex items-center gap-2">
           <M2TLogo className="w-16 h-16" />
          {/* <img src="/assets/logo/m2t-logo.svg" alt="M2T Logo" className="w-8 h-8" />*/}
          <span className="font-semibold text-3xl text-white tracking-wide">
            Million2Trillion
          </span>
        </div>

        {/* ================= Hamburger Toggle (Mobile Only) ===================== */}
        <button
          className="md:hidden block text-white text-2xl"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          ☰
        </button>

        {/* ================= SECTION 3C: CENTER BUTTONS ===================== */}
        <div className={`w-full ${isMenuOpen ? 'block' : 'hidden'} md:flex md:items-center md:w-auto`}>
          <div className="flex flex-col md:flex-row items-center md:space-x-4 gap-2 mt-4 md:mt-0 justify-center">
            {centerButtons.map((btn) => (
              <button
                key={btn}
                className="px-4 py-2 rounded-full bg-black-800 hover:border hover:bg-blue-700 text-white text-xl font-medium transition"
              >
                {btn}
              </button>
            ))}
          </div>
        </div>

        {/* ================= SECTION 3D: RIGHT SIDE ACTIONS ===================== */}
        <div className="flex items-center space-x-2">
          {/* User Dropdown */}
          <div className="relative group">
            <button
              className="px-4 py-2 rounded-full bg-gradient-to-r from-blue-500 to-blue-200 via-blue-900 text-white font-semibold transition flex items-center space-x-2"
              onClick={() => setActiveDropdown(activeDropdown === 'user' ? null : 'user')}
            >
              <FiUser />
              <span className="text-sm uppercase">
                {currentUser
                  ? isGuest
                    ? 'Explore More'
                    : `Welcome, ${currentUser.displayName || 'User'}`
                  : 'Login'}
              </span>
              <FiChevronDown />
            </button>
            {activeDropdown === 'user' && (
              <div className="absolute right-0 mt-2 bg-gray-800 p-2 rounded shadow-lg w-48 z-40">
                {!currentUser && (
                  <>
                    <div
                      className="px-3 py-2 cursor-pointer hover:bg-blue-700 rounded"
                      onClick={() => navigate('/dashboard')}
                    >
                      Guest Access
                    </div>
                    <div
                      className="px-3 py-2 cursor-pointer hover:bg-blue-700 rounded"
                      onClick={() => {
                        setStartAuthMode('signup');
                        setShowAuthModal(true);
                      }}
                    >
                      New User Sign Up
                    </div>
                    <div
                      className="px-3 py-2 cursor-pointer hover:bg-blue-700 rounded"
                      onClick={() => {
                        setStartAuthMode('login');
                        setShowAuthModal(true);
                      }}
                    >
                      Already Member Login
                    </div>
                  </>
                )}
                {currentUser && (
                  <>
                    <div
                      className="px-3 py-2 cursor-pointer hover:bg-blue-700 rounded"
                      onClick={() => navigate('/dashboard')}
                    >
                      Go to Dashboard
                    </div>
                    {!isGuest && (
                      <div
                        className="px-3 py-2 cursor-pointer hover:bg-blue-700 rounded"
                        onClick={() => {
                          setStartAuthMode('login');
                          setShowAuthModal(true);
                        }}
                      >
                        Switch Account / Sign In as Member
                      </div>
                    )}
                    <div
                      className="px-3 py-2 cursor-pointer hover:bg-blue-700 rounded"
                      onClick={handleLogout}
                    >
                      Logout
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Language Selector */}
          <div className="relative group">
            <button
              className="p-2 rounded-full border border-gray-500 hover:bg-amber-700 flex items-center"
              onClick={() =>
                setActiveDropdown(activeDropdown === 'language' ? null : 'language')
              }
            >
              <span className="text-yellow-400 font-bold text-sm">{selectedLanguage}</span>
              <FiGlobe className="ml-1" />
            </button>
            {activeDropdown === 'language' && (
              <div className="absolute top-full right-0 mt-2 bg-gray-800 p-4 rounded shadow-lg w-48 z-40 max-h-64 overflow-y-auto">
                {languages.map((lang) => (
                  <div
                    key={lang.code}
                    className={`flex items-center space-x-2 p-1 cursor-pointer hover:bg-blue-700 rounded ${
                      selectedLanguage === lang.code ? 'text-blue-400 font-bold' : ''
                    }`}
                    onClick={() => setSelectedLanguage(lang.code)}
                  >
                    <input
                      type="radio"
                      name="language"
                      checked={selectedLanguage === lang.code}
                      readOnly
                    />
                    <span>{`${lang.label} - ${lang.code}`}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </header>

      {/* ================= SECTION 4: AUTH MODAL ===================== */}
      {showAuthModal && (
        <AuthModal
          onClose={() => setShowAuthModal(false)}
          startMode={startAuthMode}
        />
      )}
    </>
  );
}
